import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

type PageTourContextValue = {
  hasActivePageTour: boolean;
  registerPageTour: () => () => void;
};

const PageTourContext = createContext<PageTourContextValue | null>(null);

// Tracks whether any page-level PageTour is currently mounted, so a single
// global tour entry point (e.g. GuideWidget) can hide itself and avoid
// showing two "take a tour" affordances on screen at once.
export const PageTourProvider = ({ children }: { children: ReactNode }) => {
  const [activeCount, setActiveCount] = useState(0);

  const registerPageTour = () => {
    setActiveCount((count) => count + 1);
    return () => setActiveCount((count) => Math.max(0, count - 1));
  };

  return (
    <PageTourContext.Provider value={{ hasActivePageTour: activeCount > 0, registerPageTour }}>
      {children}
    </PageTourContext.Provider>
  );
};

export const useHasActivePageTour = () => {
  const ctx = useContext(PageTourContext);
  return ctx?.hasActivePageTour ?? false;
};

export const useRegisterPageTour = () => {
  const ctx = useContext(PageTourContext);
  useEffect(() => {
    if (!ctx) return;
    return ctx.registerPageTour();
  }, [ctx]);
};
